import React, { useContext, useState } from 'react';
import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  CalendarOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import { Button, Modal, Space, Tooltip, message } from 'antd';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { CARTNER_CONVOS } from '../../../../constants/define/translation';
import { btnActive } from '../../../../styles';
import { MESSAGE_ERROR } from '../../../../constants/message';
import { ConfirmAction } from '../../../molecules/confirm/ConfirmAction';
import { UserContext } from '../../../../repository/context/userContext';
import { postLoginRepository } from '../../../../repository/mytm/auth/loginRepository';
import { QuestionType } from '../../../../types/cartnerConvos';
import {
  deleteQuestionRepository,
  getQuestionRepository,
  getSummaryQuestionRepository,
} from '../../../../repository/mytm/home/cartnerConvosRepository';
import { CONFIG_CONTAINER_NAME, deleteBlob } from '../../../../utils/azure';

interface ActionsColumnProps {
  record: QuestionType;
  setIsOpenModal: Function;
  setDataRecord: Function;
  onRefresh: () => void;
  setLoading?: (value: boolean) => void;
  setIsOpenScheduleModal: Function;
  setDataScheduleRecord: Function;
  setIsOpenSummaryModal: Function;
  setSummaryRecord: Function;
}

export default React.memo((props: ActionsColumnProps) => {
  const { t } = useTranslation(CARTNER_CONVOS);
  const {
    record,
    setIsOpenModal,
    setDataRecord,
    onRefresh,
    setLoading,
    setIsOpenScheduleModal,
    setDataScheduleRecord,
    setIsOpenSummaryModal,
    setSummaryRecord,
  } = props;
  const userContext = useContext(UserContext);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    Modal.confirm({
      title: t('table.popup.title'),
      okText: t('table.popup.okBtn'),
      cancelText: t('table.popup.cancelBtn'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <ConfirmAction
          textConfirmAction={t('table.popup.delete')}
          isConfirmPassword
          textConfirmPassword={t('table.popup.confirmPassword')}
        />
      ),
      onOk() {
        setDeleting(true);
        const updateFunc = () => {
          deleteQuestionRepository(record?.id)
            .then(res => {
              deleteBlob(record?.imageUrl, CONFIG_CONTAINER_NAME.UPLOADS).then().catch();
              message.success(t('message.success.deleteQuestion'));
            })
            .catch(() => message.error(MESSAGE_ERROR.M02))
            .finally(() => {
              onRefresh();
              setDeleting(false);
            });
        };
        const inputPasswordNode = document.getElementById('inputPasswordForConfirm');
        const password = (inputPasswordNode as any).value;
        if (!userContext.user?.email || userContext.user?.email === '') {
          setLoading && setLoading(false);
          message.error(MESSAGE_ERROR.M02);
        } else {
          postLoginRepository({
            email: userContext.user?.email,
            password,
          })
            .then(() => {
              updateFunc();
            })
            .catch((e: any) => {
              setDeleting(false);
              message.error(e?.errorMessage);
            });
        }
      },
    });
  };

  const handleEdit = () => {
    Modal.confirm({
      title: t('table.popup.title'),
      okText: t('table.popup.okBtn'),
      cancelText: t('table.popup.cancelBtn'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <ConfirmAction
          textConfirmAction={t('table.popup.edit')}
          isConfirmPassword
          textConfirmPassword={t('table.popup.confirmPassword')}
        />
      ),
      onOk() {
        setLoading && setLoading(true);
        const findOneFunc = () => {
          getQuestionRepository(record?.id)
            .then(res => {
              if (res && res.question) {
                if (res.question?.isAnswered) {
                  message.error(t('message.error.isAnswered'));
                  setLoading && setLoading(false);
                  return;
                }
                setDataRecord(res.question);
                setLoading && setLoading(false);
                setIsOpenModal(true);
              }
            })
            .catch(() => message.error(MESSAGE_ERROR.M02))
            .finally(() => {});
        };
        const inputPasswordNode = document.getElementById('inputPasswordForConfirm');
        const password = (inputPasswordNode as any).value;
        if (!userContext.user?.email || userContext.user?.email === '') {
          setLoading && setLoading(false);
          message.error(MESSAGE_ERROR.M02);
        } else {
          postLoginRepository({
            email: userContext.user?.email,
            password,
          })
            .then(() => {
              findOneFunc();
            })
            .catch((e: any) => {
              message.error(e?.errorMessage);
              setLoading && setLoading(false);
            })
            .finally(() => {});
        }
      },
      onCancel() {
        // NOT
      },
    });
  };

  const handleSummary = () => {
    setLoading && setLoading(true);
    getSummaryQuestionRepository(record.id)
      .then(res => {
        if (res && res.question) {
          setSummaryRecord(res.question);
          setLoading && setLoading(false);
          setIsOpenSummaryModal(true);
        }
      })
      .catch(() => message.error(t('message.error.summaryQuestion')))
      .finally(() => {});
  };

  const handleUpdateSchedule = () => {
    Modal.confirm({
      title: t('table.popup.title'),
      okText: t('table.popup.okBtn'),
      cancelText: t('table.popup.cancelBtn'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <ConfirmAction
          textConfirmAction={t('table.popup.confirmSchedule')}
          isConfirmPassword
          textConfirmPassword={t('table.popup.confirmPassword')}
        />
      ),
      onOk() {
        setLoading && setLoading(true);
        const showScheduleFunc = () => {
          setDataScheduleRecord({
            questionTitle: record.title,
            ...record.questionSchedule,
            pollQuestionId: record.id,
          });
          setIsOpenScheduleModal(true);
        };
        const inputPasswordNode = document.getElementById('inputPasswordForConfirm');
        const password = (inputPasswordNode as any).value;
        if (!userContext.user?.email || userContext.user?.email === '') {
          setLoading && setLoading(false);
          message.error(MESSAGE_ERROR.M02);
        } else {
          postLoginRepository({
            email: userContext.user?.email,
            password,
          })
            .then(() => {
              showScheduleFunc();
            })
            .catch((e: any) => {
              message.error(e?.errorMessage);
            })
            .finally(() => {
              setLoading && setLoading(false);
            });
        }
      },
      onCancel() {
        // NOT
      },
    });
  };

  return (
    <StyledContainer>
      <Space size="small">
        <Tooltip title={t('table.tooltip.edit')}>
          <Button icon={<EditOutlined />} onClick={handleEdit} />
        </Tooltip>
        <Tooltip title={t('table.tooltip.delete')}>
          <Button
            icon={<DeleteOutlined style={{ color: '#ff4d4f' }} />}
            onClick={handleDelete}
            disabled={deleting}
            loading={deleting}
          />
        </Tooltip>
        <Tooltip title={t('table.tooltip.summary')}>
          <Button icon={<BarChartOutlined />} onClick={handleSummary} />
        </Tooltip>
        <Tooltip title={t('table.tooltip.schedule')}>
          <Button icon={<CalendarOutlined />} onClick={handleUpdateSchedule} />
        </Tooltip>
      </Space>
    </StyledContainer>
  );
});

const StyledContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-right: 0.5rem;
  ${btnActive}
`;
